import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import Swal from 'sweetalert2';

@Injectable({
  providedIn: 'root'
})
export class AffiliationLockService {

  private readonly SESSION_KEY = 'tabId';
  private tabId: string;
  private currentAffiliationId: string | null = null;
  private currentStorageKey: string | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document
  ) {
    // ID único para esta pestaña
    const existingId = sessionStorage.getItem(this.SESSION_KEY);
    if (existingId) {
      this.tabId = existingId;
    } else {
      this.tabId = Date.now().toString() + '-' + Math.random().toString(16).slice(2);
      sessionStorage.setItem(this.SESSION_KEY, this.tabId);
    }
  }

  /**
   * Inicia el control de "solo una pestaña" para UNA afiliación específica
   */
  startLock(affiliationId: string,message: string): void {
    this.currentAffiliationId = affiliationId;
    this.currentStorageKey = this.getStorageKey(affiliationId);

    const activeTab = localStorage.getItem(this.currentStorageKey);

    if (!activeTab) {
      // Nadie es dueño aún → esta pestaña toma el control
      localStorage.setItem(this.currentStorageKey, this.tabId);
    } else if (activeTab !== this.tabId) {
      // Ya existe otra pestaña dueña de ESTA afiliación → bloquear esta
      this.blockCurrentTab(message);
    }

    // Escuchar cambios de localStorage (cuando otra pestaña tome/queme el control de ESTA afiliación)
    window.addEventListener('storage', this.onStorageChange);

    // Al cerrar/recargar pestaña, liberar el lock si esta era la dueña
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  /**
   * Detiene el control para la afiliación actual
   */
  stopLock(): void {
    if (this.currentStorageKey) {
      const activeTab = localStorage.getItem(this.currentStorageKey);
      if (activeTab === this.tabId) {
        // Si esta pestaña era la dueña de esa afiliación, libera el lock
        localStorage.removeItem(this.currentStorageKey);
      }
    }

    window.removeEventListener('storage', this.onStorageChange);
    window.removeEventListener('beforeunload', this.onBeforeUnload);

    this.unblockCurrentTab();
    this.currentAffiliationId = null;
    this.currentStorageKey = null;
  }

  // --------------------------------------------------------
  // PRIVADOS
  // --------------------------------------------------------

  private getStorageKey(affiliationId: string): string {
    return `affiliation_lock_${affiliationId}`;
  }

  private onStorageChange = (event: StorageEvent ) => {
    if (!this.currentStorageKey || event.key !== this.currentStorageKey) {
      return;
    }

    const activeTab = localStorage.getItem(this.currentStorageKey);

    if (activeTab === this.tabId) {
      this.unblockCurrentTab();
    } else {
      this.blockCurrentTab('Acción');
    }
  };

  private onBeforeUnload = () => {
    if (!this.currentStorageKey) return;

    const activeTab = localStorage.getItem(this.currentStorageKey);
    if (activeTab === this.tabId) {
      // Si esta pestaña era la dueña de ESTA afiliación, libera el lock al cerrarse
      localStorage.removeItem(this.currentStorageKey);
    }
  };

  private blockCurrentTab(message: string): void {

    this.document.body.style.pointerEvents = 'none';
    this.document.body.style.opacity = '0.9';

    Swal.fire({
      icon: 'error',
      title: `${message} en uso`,
      text:  `Esta ${message.toLowerCase()} ya está siendo gestionada en otra pestaña. Solo se permite una pestaña por ${message.toLowerCase()}. `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false,
      showConfirmButton: false
    });
  }

  private unblockCurrentTab(): void {
    this.document.body.style.pointerEvents = 'auto';
    this.document.body.style.opacity = '1';
  }
}
